/**
 * Serializes RPCs while reserving the connection for an explicit transaction or pause.
 *
 * Every wait in here is bounded, because every tab's database work funnels through one of these
 * and a single stuck call used to park all of them forever:
 *
 *   - WAITING for the connection is bounded (`waitDeadlineMs`). A call whose wait expires is
 *     removed from the line and NEVER runs afterwards — its caller has already been told it failed,
 *     and a write landing after that "failure" is the worst outcome of all.
 *   - RUNNING is bounded (`runDeadlineMs`, per call). A call that overruns may still be executing
 *     on the connection, so nothing else may start on it: the queue is POISONED — every waiter and
 *     every later call fails at once — and `onPoisoned` tells the owner to replace the resource.
 *     Rejecting only the late caller while the next call starts would interleave two statements on
 *     one connection; keeping the line parked until the straggler settles is the hang this replaces.
 *   - A RESERVATION (a tab's open transaction or pause window) that sits idle longer than
 *     `reservationIdleDeadlineMs` poisons the queue the same way: a `begin` whose tab never
 *     commits must not park every other tab. The owner's replacement of the resource rolls the
 *     abandoned transaction back.
 *   - Cleanup after a departed tab (its rollback) and the departure itself are bounded too.
 */
import {
  boundedPromise,
  DeadlineExceededError,
  notifyDeadlineExceeded,
  withDeadline,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Defaults for a consumer that passes no bounds — the page-side acknowledgement serializer in
 * `open-proxied-turso-connection.web.ts`, whose calls are RPC round trips already bounded by the
 * leader request queue (at most its handover budget plus a five-minute per-request cap). These
 * are backstops sized ABOVE that, so they never fire on legitimate work; a consumer that owns the
 * resource passes tight values.
 */
const DEFAULT_WAIT_DEADLINE_MS = 15 * 60_000;
const DEFAULT_RUN_DEADLINE_MS = 15 * 60_000;

interface QueuedCall {
  ends: boolean;
  fail(error: unknown): void;
  owner: string;
  requiresUnowned: boolean;
  /** Hands the connection to this call. */
  start(): void;
  starts: boolean;
}

export interface LeaderCallQueueOptions {
  /** Bound on the departed-owner rollback. Defaults to `runDeadlineMs`. */
  cleanupDeadlineMs?: number;
  /**
   * Bound on `tabClosed()` waiting for the departed tab's reservation to be released (its running
   * call finishing, then its rollback). Defaults to `waitDeadlineMs + cleanupDeadlineMs`.
   */
  departureDeadlineMs?: number;
  /**
   * Called once when the queue is poisoned (a run or a reservation overran its deadline, or a
   * rollback failed). The connection must be replaced before anything uses it again.
   */
  onPoisoned?(cause: unknown): void;
  /**
   * How long a reservation may sit with none of its owner's calls running or runnable. Omitted:
   * reservations are not timed (the page-side serializer, whose single owner is always the tab
   * itself).
   */
  reservationIdleDeadlineMs?: number;
  /** Default bound on one call's run; `run()` can override it per call. */
  runDeadlineMs?: number;
  /** Bound on one call's wait for the connection. */
  waitDeadlineMs?: number;
}

export function createLeaderCallQueue(
  rollback: () => Promise<void>,
  options: LeaderCallQueueOptions = {},
) {
  const waitDeadlineMs = options.waitDeadlineMs ?? DEFAULT_WAIT_DEADLINE_MS;
  const defaultRunDeadlineMs = options.runDeadlineMs ?? DEFAULT_RUN_DEADLINE_MS;
  const cleanupDeadlineMs = options.cleanupDeadlineMs ?? defaultRunDeadlineMs;
  const departureDeadlineMs = options.departureDeadlineMs ?? waitDeadlineMs + cleanupDeadlineMs;
  const pending: QueuedCall[] = [];
  const departed = new Set<string>();
  const departures = new Map<string, { reject(error: unknown): void; resolve(): void }>();
  let owner: string | null = null;
  // A call or a rollback holds the connection.
  let running = false;
  let failure: unknown = null;
  let poisonReported = false;
  let reservationTimer: ReturnType<typeof setTimeout> | null = null;

  function clearReservationTimer(): void {
    if (reservationTimer !== null) {
      clearTimeout(reservationTimer);
      reservationTimer = null;
    }
  }

  /** Fail everything waiting, now and in future. `poisoned` also tells the owner to replace it. */
  function fail(error: unknown, poisoned: boolean): void {
    failure ??= error;
    clearReservationTimer();
    for (const call of pending.splice(0)) {
      call.fail(failure);
    }
    for (const departure of departures.values()) {
      departure.reject(failure);
    }
    departures.clear();
    if (poisoned && !poisonReported) {
      poisonReported = true;
      try {
        options.onPoisoned?.(error);
      } catch {
        // The owner's recovery failing cannot un-poison the queue.
      }
    }
  }

  function armReservationTimer(): void {
    const idleMs = options.reservationIdleDeadlineMs;
    if (idleMs === undefined || owner === null || running || reservationTimer !== null) {
      return;
    }
    const idleOwner = owner;
    reservationTimer = setTimeout(() => {
      reservationTimer = null;
      if (owner !== idleOwner || running) {
        return;
      }
      const error = new DeadlineExceededError(
        `Database connection reserved by an idle transaction or pause (tab ${idleOwner})`,
        idleMs,
      );
      notifyDeadlineExceeded(error);
      fail(error, true);
    }, idleMs);
  }

  function drain(): void {
    if (running) {
      return;
    }
    if (failure !== null) {
      fail(failure, false);
      return;
    }
    if (owner === null) {
      clearReservationTimer();
    }
    if (owner !== null && departed.has(owner)) {
      running = true;
      clearReservationTimer();
      void withDeadline(
        { deadline: cleanupDeadlineMs, operation: "roll back a departed tab's reservation" },
        rollback,
      ).then(
        () => {
          owner = null;
          running = false;
          drain();
        },
        (error: unknown) => {
          // Never run another client's work inside a transaction whose rollback failed (or may
          // still be running). The resource must be replaced before reuse.
          running = false;
          fail(error, true);
        },
      );
      return;
    }
    for (const [tabId, departure] of departures) {
      if (owner !== tabId) {
        departures.delete(tabId);
        departure.resolve();
      }
    }
    const index = pending.findIndex(
      (call) => owner === null || (!call.requiresUnowned && call.owner === owner),
    );
    if (index === -1) {
      // Nothing may run: if that is because a reservation is waiting on its owner, time it.
      armReservationTimer();
      return;
    }
    const call = pending.splice(index, 1)[0];
    if (call === undefined) {
      return;
    }
    if (departed.has(call.owner)) {
      call.fail(new Error("Database client closed before its queued call executed"));
      drain();
      return;
    }
    running = true;
    clearReservationTimer();
    call.start();
  }

  return {
    close: (error: unknown) => {
      fail(error, false);
    },
    run: async (
      tabId: string,
      starts: boolean,
      ends: boolean,
      run: () => Promise<unknown>,
      requiresUnowned = false,
      callOptions: { operation?: string; runDeadlineMs?: number } = {},
    ): Promise<unknown> => {
      const operation = callOptions.operation ?? "database call";
      await boundedPromise<void>(
        { deadline: waitDeadlineMs, operation: `${operation} (waiting for the connection)` },
        (resolve, reject) => {
          if (failure !== null) {
            reject(failure);
            return;
          }
          if (departed.has(tabId)) {
            reject(new Error("Database client is closed"));
            return;
          }
          const call: QueuedCall = {
            ends,
            fail: reject,
            owner: tabId,
            requiresUnowned,
            start: resolve,
            starts,
          };
          pending.push(call);
          drain();
          return () => {
            // Settled by any route. If it gave up while still in line, leave the line: it must
            // never run after its caller was told it failed.
            const position = pending.indexOf(call);
            if (position !== -1) {
              pending.splice(position, 1);
            }
          };
        },
      );

      // This call now holds the connection — unless the queue closed in the turn between.
      if (failure !== null) {
        throw failure;
      }
      const previousOwner = owner;
      if (starts) {
        owner = tabId;
      }
      let workSettled = false;
      const execution = (async () => run())();
      execution.then(
        () => {
          workSettled = true;
        },
        () => {
          workSettled = true;
        },
      );
      try {
        const result = await withDeadline(
          { deadline: callOptions.runDeadlineMs ?? defaultRunDeadlineMs, operation },
          async () => execution,
        );
        if (ends) {
          owner = null;
        }
        running = false;
        drain();
        return result;
      } catch (error) {
        if (!workSettled) {
          // Overran: it may still be executing on the connection, so nothing else may start.
          fail(error, true);
          throw error;
        }
        // A failed BEGIN did not acquire the transaction; a failed COMMIT
        // still needs the owner's ROLLBACK, so retain ownership in that case.
        if (starts) {
          owner = previousOwner;
        }
        running = false;
        drain();
        throw error;
      }
    },
    tabClosed: async (tabId: string) => {
      departed.add(tabId);
      for (let index = pending.length - 1; index >= 0; index--) {
        const call = pending[index];
        if (call?.owner === tabId) {
          pending.splice(index, 1);
          call.fail(new Error("Database client closed before its queued call executed"));
        }
      }
      await boundedPromise<void>(
        {
          deadline: departureDeadlineMs,
          operation: `release the connection reserved by departed tab ${tabId}`,
        },
        (resolve, reject) => {
          if (failure !== null) {
            reject(failure);
            return;
          }
          const departure = { reject, resolve };
          departures.set(tabId, departure);
          drain();
          return () => {
            if (departures.get(tabId) === departure) {
              departures.delete(tabId);
            }
          };
        },
      );
    },
  };
}
